import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useStyles } from './CloudPartnerDetails.styles'
import {
  AutoComplete,
  ExternalLink,
  TextInputV2,
} from '@labrav/react-components'
import { Sections } from '../../../modules/flyOutOnboarding/reducer'
import { FormikProps } from 'formik'
import { FormLockedType } from '../../../types/FormLockedType'
import { Typography } from '@mui/material'
import { fetchAwsCountriesList } from '../../../common/api'
import { AwsCountriesList } from '../../AddEditProductFormComponents/FlyOutOfferAvailabilityByCountry/FlyOutProductOfferAvailabilityByCountry'
import { Options } from '@labrav/react-components/lib/@types/v2/components/AutoComplete'
import { awsSellerBusinessLocationUrl } from '../../../common/utils/constants'

export type FlyOutCloudSettingsProps = {
  cloudSettings: FormikProps<Sections>
}
export const CloudPartnerDetails: React.FC<
  FlyOutCloudSettingsProps & FormLockedType
> = ({ cloudSettings, isFormLocked }) => {
  const { classes } = useStyles()
  const { values, errors, touched, handleBlur, setFieldValue } = cloudSettings
  const cloudSettingsValues = values.cloudSettings
  const cloudSettingsErrors = errors.cloudSettings
  const cloudSettingsTouched = touched.cloudSettings
  const [awsCountriesLists, setAwsCountriesList] = useState<AwsCountriesList>({
    countries: [],
  })

  useEffect(() => {
    fetchAwsCountriesList()
      .then(response => {
        setAwsCountriesList(response.data)
      })
      .catch(error => {
        console.error('Failed to fetch countries list:', error)
      })
  }, [])

  const handleCountrySelection = useCallback(
    async (
      event: React.SyntheticEvent,
      newValue: string | { key: string | undefined; label: string } | null
    ) => {
      const cloudSettings = {
        ...values.cloudSettings,
        countryRegistered: newValue
          ? (newValue as { key: string | undefined; label: string }).key
          : '',
      }
      await setFieldValue('cloudSettings', cloudSettings)
    },
    [values, setFieldValue]
  )
  const allCountryNames = useMemo(
    () =>
      awsCountriesLists?.countries?.map(country => ({
        key: country.code,
        label: country.country,
      })) || [],
    [awsCountriesLists?.countries]
  )
  const selectedCountryValue = useMemo(
    () =>
      allCountryNames.find(
        country => country.key === cloudSettingsValues?.countryRegistered
      ) || { key: '', label: '' },
    [allCountryNames, cloudSettingsValues?.countryRegistered]
  )
  return (
    <div className={classes.fieldsContainer}>
      <div className={classes.textContainer}>
        <div>
          <Typography className={classes.sectionText}>
            Please fill your 12-digit AWS account ID.
          </Typography>
        </div>
        <div className={classes.field}>
          <TextInputV2
            keyName="cloudSettings.cloudAccountId"
            title="AWS account ID"
            type="text"
            placeHolder=""
            value={cloudSettings.values.cloudSettings.cloudAccountId}
            isError={
              !!(
                cloudSettings.errors.cloudSettings?.cloudAccountId &&
                cloudSettings.touched.cloudSettings?.cloudAccountId
              )
            }
            onChange={cloudSettings.handleChange}
            isReadOnly={isFormLocked as boolean}
            isRequired={false}
            errorText={
              (!!cloudSettings.touched.cloudSettings?.cloudAccountId &&
                cloudSettings.errors.cloudSettings?.cloudAccountId) ||
              ''
            }
            showSpinner={false}
            onBlur={cloudSettings.handleBlur}
          />
        </div>
      </div>
      <div className={classes.textContainer}>
        <div className={classes.field}>
          <TextInputV2
            keyName="cloudSettings.cloudAccountName"
            title="AWS account name"
            type="text"
            placeHolder=""
            value={cloudSettings.values.cloudSettings.cloudAccountName}
            isError={
              !!(
                cloudSettings.errors.cloudSettings?.cloudAccountName &&
                cloudSettings.touched.cloudSettings?.cloudAccountName
              )
            }
            onChange={cloudSettings.handleChange}
            onBlur={cloudSettings.handleBlur}
            isReadOnly={isFormLocked as boolean}
            isRequired={false}
            errorText={
              (!!cloudSettings.touched.cloudSettings?.cloudAccountName &&
                cloudSettings.errors.cloudSettings?.cloudAccountName) ||
              ''
            }
            supportingText={
              'E.g. marketplace_account or seller_environment or abcxyz123'
            }
            showSpinner={true}
          />
        </div>
        <div className={classes.field}>
          <AutoComplete
            onChange={handleCountrySelection}
            id={'cloudSettings.countryRegistered'}
            label={'Select a country'}
            options={allCountryNames as Options[]}
            value={selectedCountryValue}
            error={
              !!(
                cloudSettingsErrors?.countryRegistered &&
                cloudSettingsTouched?.countryRegistered
              )
            }
            helperText={
              !!cloudSettingsTouched?.countryRegistered &&
              cloudSettingsErrors?.countryRegistered &&
              cloudSettingsErrors?.countryRegistered
            }
            onBlur={handleBlur}
            disabled={isFormLocked}
            viewOnly={false}
          />
          <div className={classes.subText}>
            Select the country that is added as the{' '}
            <span>
              {' '}
              <ExternalLink
                href={awsSellerBusinessLocationUrl}
                children={
                  <span className={classes.urlBoldText}>Business location</span>
                }
              />
            </span>{' '}
            on AWS Marketplace management portal.
          </div>
        </div>
      </div>
    </div>
  )
}
